// The panel over the 3D view: what the compiler said, what the board is
// saying, and what is said back to it.
//
// The serial half polls rather than being pushed to. `get_uart_at` returns the
// board's whole transcript (the controller caps it at 64 kB), so each poll is a
// full re-read and the panel is simply set to it -- there is no incremental
// state to keep in sync with a board that can reboot underneath us.
//
// The input half is that in reverse: `send_uart_at` hands bytes to the board's
// USART, which delivers them at the sketch's baud rate as the simulation runs.
// Nothing typed is echoed into the transcript, because the transcript is only
// what the board transmitted -- what comes back is whatever the sketch chooses
// to print, exactly as on a board with no local echo.
//
// Under the transcript goes the board's hardware warning, polled alongside it.
// switches.ts shows the same one, but only for a robot with switches or
// joysticks, and the one that matters most here needs neither: an S3 sketch
// that takes GPIO19/20 loses the USB port, and a transcript that simply stops
// does not say why.

import { InterfaceHybrid } from "simulator";

const POLL_MS = 100;

// What a typed line is terminated with. `Serial.readStringUntil('\n')` and
// `Serial.parseInt` both need a terminator to know the line has ended, and a
// sketch reading byte by byte is no worse off for it.
const LINE_ENDING = "\n";

let hybrid: InterfaceHybrid | undefined;
// Which articulated's controller is being watched. The editor drives the most
// recently added robot, and so does this.
let robot = 0;
let timer: number | undefined;

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Watch the board running articulated `index`, showing the panel if it is not
// already up. Re-pointing it at another robot drops the previous one's output.
export function watchRobot(scene: InterfaceHybrid, index: number) {
  hybrid = scene;
  robot = index;
  element("monitor").hidden = false;
  element("monitor-serial").textContent = "";
  element("monitor-warning").hidden = true;

  if (timer === undefined) {
    // The buttons are wired here rather than at import time: this module is
    // pulled in from index.ts, which runs before anything guarantees the panel
    // is in the document.
    element("monitor-close").addEventListener("click", () => {
      element("monitor").hidden = true;
    });
    element("monitor-input").addEventListener("submit", send);
    // Ticking it back on catches up at once, rather than on the next line --
    // which, from a quiet sketch, may never come.
    const autoscroll = element<HTMLInputElement>("monitor-autoscroll");
    autoscroll.addEventListener("change", () => {
      if (autoscroll.checked) {
        const serial = element("monitor-serial");
        serial.scrollTop = serial.scrollHeight;
      }
    });
    timer = window.setInterval(poll, POLL_MS);
  }
}

// Type the input box at the board, and clear it -- the way pressing enter in a
// serial monitor does. An empty box still sends its line ending, since that is
// all a sketch waiting on `readStringUntil` needs to be released.
function send(event: Event) {
  event.preventDefault();
  const input = element<HTMLInputElement>("monitor-send");
  const line = input.value;
  input.value = "";
  if (!hybrid) {
    return;
  }
  try {
    hybrid.send_uart_at(robot, line + LINE_ENDING);
  } catch (error) {
    console.error("serial monitor: could not send", error);
  }
}

function poll() {
  if (!hybrid) {
    return;
  }
  const serial = element("monitor-serial");
  try {
    const text = hybrid.get_uart_at(robot);
    if (text !== serial.textContent) {
      serial.textContent = text;
      // Follow the tail, the way a serial monitor does -- unless the user has
      // asked it to hold still so they can read back.
      if (element<HTMLInputElement>("monitor-autoscroll").checked) {
        serial.scrollTop = serial.scrollHeight;
      }
    }
    const warning = element("monitor-warning");
    const found = hybrid.hardware_warning_at(robot) ?? "";
    if (found !== warning.textContent) {
      warning.textContent = found;
    }
    warning.hidden = found === "";
  } catch (error) {
    // wasm is unusable after a panic, so one crash would otherwise flood the
    // console every 100 ms. Stop, and leave what was already printed on screen.
    window.clearInterval(timer);
    timer = undefined;
    console.error("serial monitor: stopped polling", error);
  }
}

// Report a build: `text` is the compiler's own output, and `failed` decides
// whether it reads as a result or as an error. Compiling always brings the
// panel back, since that is when the user is looking for it.
export function showBuild(text: string, failed = false) {
  element("monitor").hidden = false;
  element("monitor-build-section").hidden = false;
  const build = element("monitor-build");
  build.textContent = text;
  build.classList.toggle("failed", failed);
  build.scrollTop = 0;
}
